import React from 'react';
import { createPortal } from 'react-dom';
import { AuthUser } from '../utils/authApi';
import { getPermissions, ModuleId, ModuleStatus } from '../utils/permissions';
import { ChevronDown } from 'lucide-react';

type Tab = 'generator' | 'quick_invoice' | 'batch_invoice' | 'bills' | 'accounts' | 'logs' | 'profile' | 'products' | 'iam' | 'ledger' | 'pos' | 'tables' | 'dashboard' | 'employees' | 'clinic' | 'inventory' | 'fitness' | 'food';

interface NavbarProps {
  currentTab: Tab;
  setCurrentTab: (tab: Tab) => void;
  authUser: AuthUser | null;
  onSignIn: () => void;
  onSignOut: () => void;
  onOutletFilterChange?: (outletId: number | null) => void;
  planBadge?: string | null;
  moduleStatuses?: Partial<Record<ModuleId, ModuleStatus>>;
  moduleTrialEndDates?: Record<string, string>;
  modulePeriodEndDates?: Record<string, string>;
  onTrialExpired?: () => void;
}
function parseUtc(iso: string): number {
  return new Date(iso.endsWith('Z') ? iso : iso + 'Z').getTime();
}

function formatTimeLeft(expiryIso: string, now: number): string {
  const diffMs = parseUtc(expiryIso) - now;
  if (diffMs <= 0) return 'Expired';
  const totalSeconds = Math.floor(diffMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function formatQuotaTimeLeft(resetIso: string, now: number): string {
  // Parse midnight at the start of the reset date
  const resetMs = new Date(resetIso.includes('T') ? resetIso : `${resetIso}T00:00:00`).getTime();
  const diffMs = resetMs - now;
  if (diffMs <= 0) return 'Resets now';
  const totalSeconds = Math.floor(diffMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) return `${days}d ${hours}h ${minutes}m ${seconds}s`;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  return `${minutes}m ${seconds}s`;
}
export default function Navbar({ currentTab, setCurrentTab, authUser, onSignIn, onSignOut, onOutletFilterChange, moduleStatuses = {}, moduleTrialEndDates = {}, modulePeriodEndDates = {}, onTrialExpired }: NavbarProps) {
  const p = getPermissions(authUser?.role, authUser?.permissions, (authUser as any)?.plan, (authUser as any)?.plan_tier, (authUser as any)?.plan_status, moduleStatuses);

  // Basic plan has no expiry of its own — what actually "renews" is the
  // monthly invoice count reset. Fetch that date once so the plan badge
  // can show "renews in Xd" for Basic accounts instead of nothing.
  const [invoiceResetDate, setInvoiceResetDate] = React.useState<string | null>(null);
  React.useEffect(() => {
    if ((authUser as any)?.plan !== 'basic') return;
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';
    fetch(`${BASE}/invoices/monthly-usage`, { headers: { Authorization: `Bearer ${jwt}` } })
      .then(r => r.json())
      .then(d => { if (d.reset_date) setInvoiceResetDate(d.reset_date); })
      .catch(() => {});
  }, [(authUser as any)?.plan]);

  const outletId = (authUser as any)?.outlet_id;
  const outletName = (authUser as any)?.outlet_name;
  const isOutletScoped = outletId != null;
  const isOwnerOrAdmin = authUser?.role === 'owner' || authUser?.role === 'admin' || authUser?.role === 'individual';
  const canSeeDashboardAndProfile = isOwnerOrAdmin || authUser?.role === 'manager';
const [now, setNow] = React.useState(Date.now());
const firedExpiryRef = React.useRef<Set<string>>(new Set());
React.useEffect(() => {
  const interval = setInterval(() => setNow(Date.now()), 1000);
  return () => clearInterval(interval);
}, []);

// Fire an immediate refetch the moment any live countdown (Suite trial or
// module trial) actually crosses zero — don't wait for the 60s poll.
React.useEffect(() => {
  const planExpiry = (authUser as any)?.plan_expires_at;
  const trialEndValues = Object.entries(moduleStatuses || {})
  .filter(([, s]) => s === 'trial')
  .map(([id]) => moduleTrialEndDates[id])
  .filter(Boolean) as string[];

const activePeriodEndValues = Object.entries(moduleStatuses || {})
  .filter(([, s]) => s === 'active')
  .map(([id]) => modulePeriodEndDates[id])
  .filter(Boolean) as string[];

const candidates = [planExpiry, ...trialEndValues, ...activePeriodEndValues].filter(Boolean) as string[];

  candidates.forEach(iso => {
    const expiryMs = parseUtc(iso);
    if (expiryMs <= now && !firedExpiryRef.current.has(iso)) {
      firedExpiryRef.current.add(iso);
      onTrialExpired?.();
    }
  });
}, [now, authUser, moduleStatuses, moduleTrialEndDates, modulePeriodEndDates]);
const lockedModules = new Set(
    Object.entries(moduleStatuses || {})
      .filter(([, s]) => s === 'locked')
      .map(([id]) => id)
  );
  const hasModuleAccess = Object.values(moduleStatuses || {}).some(
    s => s === 'active' || s === 'trial'
  );

  // Mirrors the backend's OR condition in /outlets (module == "invoicing"):
  // an outlet is invoicing-eligible if EITHER the Suite plan is live OR the
  // standalone pos module is active/trial. Only lock the icon when BOTH are
  // false — matches suite_plan_ok || pos_outlet_ids on the server.
  const isBasicPlan = (authUser as any)?.plan === 'basic';
  const suitePlanOk = isBasicPlan || (authUser as any)?.plan_status === 'active' || (authUser as any)?.plan_status === 'trial';
  const posModuleOk = moduleStatuses?.pos === 'active' || moduleStatuses?.pos === 'trial';
  const isInvoicingLocked = !suitePlanOk && !posModuleOk;

  const isTabLocked = (tab: Tab): boolean => {
    const mod = tabModuleMap[tab];
    if (mod === 'invoicing') return isInvoicingLocked;
    return mod ? lockedModules.has(mod as ModuleId) : false;
  };
  const tabModuleMap: Partial<Record<Tab, ModuleId | 'invoicing'>> = {
  ledger: 'ledger', employees: 'employees', clinic: 'queue',
  fitness: 'gym', food: 'canteen', pos: 'pos', tables: 'pos',
  inventory: 'invoicing', products: 'invoicing',
  // These four are Suite plan features, not the standalone "pos" module —
  // mapped to the synthetic "invoicing" value so /outlets can apply the
  // Suite-tier-outlet OR pos-module-outlet logic (see outlets.py). Do NOT
  // map these back to 'pos' — that reintroduces the bug where Suite/Ultra
  // owners lose outlets that never bought the pos add-on.
  generator: 'invoicing', quick_invoice: 'invoicing',
  batch_invoice: 'invoicing', bills: 'invoicing',
};
  const tabs = [
  // ── Create & Sell ──
  p.canAccessInvoiceCreator  && { id: 'generator'     as Tab, label: '🧾 Invoice Creator' },
  p.canAccessGenerator       && { id: 'quick_invoice' as Tab, label: '⚡ Quick Invoice' },
  p.canAccessBatchGenerate   && { id: 'batch_invoice' as Tab, label: '📚 Batch Generate' },
  p.canAccessPOS       && { id: 'pos'           as Tab, label: '🖥 POS' },
  p.canAccessTables    && { id: 'tables'        as Tab, label: '🍽️ Table Management' },

  // ── Records ──
  p.canAccessBills     && { id: 'bills'         as Tab, label: '📄 Saved Invoices' },
  p.canAccessAccounts  && { id: 'accounts'      as Tab, label: '👤 Client Accounts' },
  p.canAccessLedger    && { id: 'ledger'        as Tab, label: '📒 Credit Book' },

  // ── Verticals ──
p.canAccessFitness   && { id: 'fitness'       as Tab, label: '💪 Fitness Center' },
p.canAccessFood      && { id: 'food'          as Tab, label: '🍽️ Meal Plans & Dining' },
p.canAccessClinic    && { id: 'clinic'        as Tab, label: '🎫 Queue' },

  // ── Catalog & Stock ──
  p.canAccessProducts  && { id: 'products'      as Tab, label: '📦 Product Catalog' },
  p.canAccessInventory && { id: 'inventory'     as Tab, label: '🗃️ Inventory' },

  // ── People ──
  p.canAccessEmployees && { id: 'employees'     as Tab, label: '👔 Attendance' },
  hasModuleAccess       && { id: 'iam'           as Tab, label: '👥 Team' },

  // ── Insights & Admin ──

  p.canAccessLogs      && { id: 'logs'          as Tab, label: '🕵️ Audit History' },

  // ── Always last ──
  canSeeDashboardAndProfile && { id: 'profile'  as Tab, label: '👤 Profile' },
].filter(Boolean) as { id: Tab; label: string }[];

  const [outlets, setOutlets] = React.useState<{id:number;name:string;city:string|null}[]>([]);
  const [selectedOutletFilter, setSelectedOutletFilter] = React.useState<number|null>(() => {
    const saved = localStorage.getItem('cloudairambo_outlet_filter');
    return saved ? Number(saved) : null;
  });
  const [showOutletMenu, setShowOutletMenu] = React.useState(false);
  const [showMobileMenu, setShowMobileMenu] = React.useState(false);
  const [showModulePillsMenu, setShowModulePillsMenu] = React.useState(false);
  const modulePillsRef = React.useRef<HTMLDivElement>(null);
  const modulePillsMenuRef = React.useRef<HTMLDivElement>(null);
  const [modulePillsCoords, setModulePillsCoords] = React.useState<{ top: number; right: number } | null>(null);

  React.useEffect(() => {
    if (!showModulePillsMenu || !modulePillsRef.current) return;
    const updateCoords = () => {
      const rect = modulePillsRef.current!.getBoundingClientRect();
      setModulePillsCoords({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
    };
    updateCoords();
    window.addEventListener('scroll', updateCoords, true);
    window.addEventListener('resize', updateCoords);
    return () => {
      window.removeEventListener('scroll', updateCoords, true);
      window.removeEventListener('resize', updateCoords);
    };
  }, [showModulePillsMenu]);
  const outletMenuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!showModulePillsMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (modulePillsRef.current?.contains(target)) return;
      if (modulePillsMenuRef.current?.contains(target)) return;
      setShowModulePillsMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showModulePillsMenu]);

  React.useEffect(() => {
    if (!showOutletMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (outletMenuRef.current && !outletMenuRef.current.contains(e.target as Node)) {
        setShowOutletMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showOutletMenu]);

  React.useEffect(() => {
    let cancelled = false;
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';
    const currentModule = tabModuleMap[currentTab];
    const qs = currentModule ? `?module=${currentModule}` : '';
    fetch(`${BASE}/outlets${qs}`, { headers: { Authorization: `Bearer ${jwt}` } })
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        if (!Array.isArray(data)) return;
        setOutlets(data);
        if (selectedOutletFilter && data.some(o => o.id === selectedOutletFilter)) {
          // Restored from localStorage — re-announce it upward so parent state
          // (outletId prop used across POS, KitchenDashboard link, etc.) syncs too.
          onOutletFilterChange?.(selectedOutletFilter);
        } else if (data.length > 0) {
          setSelectedOutletFilter(data[0].id);
          localStorage.setItem('cloudairambo_outlet_filter', String(data[0].id));
          onOutletFilterChange?.(data[0].id);
        } else {
          setSelectedOutletFilter(null);
          onOutletFilterChange?.(null);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [currentTab, authUser?.id]);

  const roleColor: Record<string, string> = {
    owner:            '#f59e0b',
    admin:            '#3b82f6',
    manager:          '#10b981',
    cashier:          '#8b5cf6',
    staff:            '#6b7280',
    viewer:           '#9ca3af',
    ledger_member:    '#0891b2',
    queue_member:     '#7c3aed',
    employee_member:  '#059669',
    gym_member:       '#ea580c',
    canteen_member:   '#16a34a',
};
  

  return (
    <>
    <nav style={{
      background: '#ffffff',
      borderBottom: '1px solid #f0f0f0',
      position: 'sticky',
      top: 0,
      zIndex: 50,
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    }}>
      <div className="navbar-outer-row" style={{
  padding: '0 20px',
  height: '65px',
  display: 'flex',
  alignItems: 'center',
  gap: 16,
}}>

        {/* Logo + Outlet Switcher group */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, marginRight: 8 }}>

          {/* Logo (clickable only here) */}
          <div
            onClick={() => setCurrentTab(p.canAccessInvoiceCreator ? 'generator' : 'bills')}
            style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
          >
            <img
              src="/logo-icon.png"
              alt="CloudAIRambo"
              style={{ width: 28, height: 28, borderRadius: 6, objectFit: 'cover' }}
              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <span className="navbar-brand-text" style={{ fontWeight: 700, fontSize: 16, color: '#111827', letterSpacing: '-0.01em' }}>
              CloudAIRambo
            </span>
          </div>

          {/* Outlet scope badge — non-owner/admin with exactly ONE assigned outlet */}
          {!isOwnerOrAdmin && outlets.length === 1 && (
            <span style={{
              fontSize: 10, fontWeight: 600,
              background: '#fef9ee', color: '#92400e',
              border: '1px solid #fde68a',
              padding: '2px 8px', borderRadius: 99,
            }}>
              🏪 {outlets[0].name}
            </span>
          )}

          {/* Outlet switcher — owner/admin (all branches), OR anyone with 2+ assigned outlets */}
          {(isOwnerOrAdmin || outlets.length > 1) && outlets.length > 0 && (
            <div ref={outletMenuRef} style={{ position: 'relative' }}>
              <button
                onClick={e => { e.stopPropagation(); setShowOutletMenu(v => !v); }}
                style={{
                  fontSize: 10, fontWeight: 600,
                  background: selectedOutletFilter ? '#eff6ff' : '#f8fafc',
                  color: selectedOutletFilter ? '#1d4ed8' : '#64748b',
                  border: `1px solid ${selectedOutletFilter ? '#bfdbfe' : '#e2e8f0'}`,
                  padding: '3px 9px', borderRadius: 99,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3,
                }}
              >
                🏪 <span className="navbar-outlet-name">{outlets.find(o => o.id === selectedOutletFilter)?.name || 'Select Branch'}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
              {showOutletMenu && (
                <div
                  onClick={e => e.stopPropagation()}
                  style={{
                    position: 'absolute', top: 'calc(100% + 6px)', left: 0,
                    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.08)', zIndex: 200,
                    minWidth: 170, maxWidth: 'min(240px, calc(100vw - 24px))', overflow: 'hidden',
                    fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  }}
                >
                  {outlets.map(o => ({ ...o, name: `🏪 ${o.name}` })).map((o, i) => (
                    <div
                      key={o.id ?? 'all'}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedOutletFilter(o.id);
                        setShowOutletMenu(false);
                        localStorage.setItem('cloudairambo_outlet_filter', String(o.id));
                        onOutletFilterChange?.(o.id);
                      }}
                      style={{
                        padding: '8px 14px', fontSize: 12,
                        fontWeight: selectedOutletFilter === o.id ? 600 : 400,
                        cursor: 'pointer',
                        background: selectedOutletFilter === o.id ? '#eff6ff' : 'transparent',
                        color: selectedOutletFilter === o.id ? '#1d4ed8' : '#374151',
                        borderTop: i === 0 ? 'none' : '1px solid #f1f5f9',
                      }}
                      onMouseEnter={e => { if (selectedOutletFilter !== o.id) (e.currentTarget as HTMLElement).style.background = '#f8fafc'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = selectedOutletFilter === o.id ? '#eff6ff' : 'transparent'; }}
                    >
                      {o.name}{o.city ? ` · ${o.city}` : ''}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Nav tabs — thin scrollbar */}
<style>{`
  .ca-nav-tabs::-webkit-scrollbar { height: 4px; }
  .ca-nav-tabs::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 99px; }
  .ca-nav-tabs::-webkit-scrollbar-thumb { background: #6366f1; border-radius: 99px; }
  .navbar-hamburger { display: none; }

  @media (max-width: 768px) {
    .navbar-trial-pills { display: none !important; }
    .navbar-brand-text { display: none !important; }
    .navbar-outlet-name { max-width: 40px !important; overflow: hidden !important; text-overflow: ellipsis !important; white-space: nowrap !important; }
    .navbar-outer-row { padding: 0 8px !important; gap: 6px !important; }
    .navbar-plan-badge { font-size: 7px !important; padding: 1px 4px !important; white-space: nowrap !important; }
    .navbar-user-name { font-size: 12px !important; }
    .navbar-role-text { font-size: 8px !important; }
    .ca-nav-tabs { display: none !important; }
    .navbar-hamburger { display: flex !important; }
    nav .navbar-outer-row > div:last-child { gap: 6px !important; }
  }
`}</style>
        <div style={{ position: 'relative', flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
          <div className="ca-nav-tabs" style={{
            flex: 1, overflowX: 'auto', display: 'flex', alignItems: 'center', gap: 2, minWidth: 0,
            scrollbarWidth: 'thin', scrollbarColor: '#d1d5db transparent',
          }}>
            {tabs.map(tab => {
              const active = currentTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setCurrentTab(tab.id)}
                  style={{
                    background: active ? '#f3f4f6' : 'none',
                    border: 'none',
                    padding: '6px 13px',
                    fontSize: 15,
                    fontWeight: active ? 600 : 400,
                    color: active ? '#111827' : isTabLocked(tab.id) ? '#dc2626' : '#6b7280',
                    cursor: 'pointer',
                    borderRadius: 99,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    position: 'relative',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { if (!active) (e.currentTarget as HTMLElement).style.color = '#374151'; }}
                  onMouseLeave={e => { if (!active) (e.currentTarget as HTMLElement).style.color = '#6b7280'; }}
                >
                  {isTabLocked(tab.id) ? '🔒 ' : ''}{tab.label}
                </button>
              );
            })}
          </div>

          <button
            className="navbar-hamburger"
            onClick={() => setShowMobileMenu(v => !v)}
            aria-label="Open menu"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              width: 36, height: 36, alignItems: 'center', justifyContent: 'center',
              fontSize: 20, color: '#374151', flexShrink: 0,
            }}
          >
            ☰
          </button>

          {showMobileMenu && (
            <div style={{
              position: 'fixed', top: 65, left: 8, right: 8,
              background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 200,
              maxHeight: '70vh', overflowY: 'auto',
              fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            }}>
              {tabs.map(tab => {
                const active = currentTab === tab.id;
                return (
                  <div
                    key={tab.id}
                    onClick={() => { setCurrentTab(tab.id); setShowMobileMenu(false); }}
                    style={{
                      padding: '12px 16px', fontSize: 15,
                      fontWeight: active ? 600 : 400,
                      color: active ? '#111827' : isTabLocked(tab.id) ? '#dc2626' : '#374151',
                      background: active ? '#f3f4f6' : 'transparent',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f1f5f9',
                    }}
                  >
                    {isTabLocked(tab.id) ? '🔒 ' : ''}{tab.label}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* User */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          {authUser ? (
            <>
              <div style={{ textAlign: 'right', lineHeight: 1.3 }}>
               <div className="navbar-user-name" style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>{authUser.name}</div>
<div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
  <div className="navbar-role-text" style={{
    fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
    color: roleColor[authUser.role] || '#6b7280',
  }}>
    {authUser.role}
  </div>

  {(() => {
  const anyModuleInTrial = Object.values(moduleStatuses || {}).some(s => s === 'trial');
  const labels: Record<ModuleId, string> = {
    ledger: '📒 Credit Book', employees: '👥 Employees',
    queue: '🎫 Queue', gym: '🏋️ Fitness Center', canteen: '🍽️ Meal Plans & Dining',
    pos: '🖥 POS',
  };
  // One combined trial countdown shared across every module currently in trial
  const moduleRows = (['ledger','employees','queue','gym','canteen','pos'] as ModuleId[])
    .filter(id => {
      const status = moduleStatuses?.[id];
      return status === 'active' || status === 'locked' || status === 'trial';
    })
    .map(id => {
      const status = moduleStatuses?.[id] as ModuleStatus;
      const locked = status === 'locked';
      const trial = status === 'trial';
      const badgeText = locked
        ? '🔒 Locked'
        : trial
          ? `⏳ Trial · ${moduleTrialEndDates[id] ? formatTimeLeft(moduleTrialEndDates[id], now) : ''} left`
          : `✓ ${modulePeriodEndDates[id] ? formatTimeLeft(modulePeriodEndDates[id], now) : 'Active'}`;
      const badgeColor = locked ? '#dc2626' : trial ? '#b45309' : '#16a34a';
      return (
        <div key={id} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '9px 12px', gap: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <span style={{ fontSize: 15 }}>{labels[id].match(/^[^\s]+/)?.[0]}</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#1f2937', whiteSpace: 'nowrap' }}>
              {labels[id].replace(/^[^\s]+\s/, '')}
            </span>
          </div>
          <span style={{
            fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
            color: badgeColor,
          }}>
            {badgeText}
          </span>
        </div>
      );
    });

  const lockedCount = (['ledger','employees','queue','gym','canteen','pos'] as ModuleId[])
    .filter(id => moduleStatuses?.[id] === 'locked').length;
  const trialCount = (['ledger','employees','queue','gym','canteen','pos'] as ModuleId[])
    .filter(id => moduleStatuses?.[id] === 'trial').length;

  return (
    <div className="navbar-trial-pills" ref={modulePillsRef} style={{ display: 'flex', alignItems: 'center', gap: 4, position: 'relative' }}>
      {(moduleRows.length > 0 || (authUser as any)?.plan === 'basic') && (
        <div
          onClick={e => { e.stopPropagation(); setShowModulePillsMenu(v => !v); }}
          style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '0.02em',
            padding: '3px 9px', borderRadius: 99, cursor: 'pointer',
            background: lockedCount > 0 ? '#fef2f2' : trialCount > 0 ? '#fffbeb' : '#f1f5f9',
            color: lockedCount > 0 ? '#dc2626' : trialCount > 0 ? '#b45309' : '#475569',
            border: `1px solid ${lockedCount > 0 ? '#fecaca' : trialCount > 0 ? '#fde68a' : '#e2e8f0'}`,
            display: 'flex', alignItems: 'center', gap: 4,
          }}
        >
          {lockedCount > 0 ? '🔒' : trialCount > 0 ? '⏳' : '✓'} {moduleRows.length} module{moduleRows.length > 1 ? 's' : ''}
          <span style={{ fontSize: 8, opacity: 0.7 }}>{showModulePillsMenu ? '▲' : '▼'}</span>
        </div>
      )}
      {showModulePillsMenu && modulePillsCoords && createPortal(
        <div ref={modulePillsMenuRef} style={{
          position: 'fixed', top: modulePillsCoords.top, right: modulePillsCoords.right,
          background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
          boxShadow: '0 12px 32px rgba(0,0,0,0.12)', zIndex: 9999,
          minWidth: 240, overflow: 'hidden',
          fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        }}>
          {(() => {
            const p2 = (authUser as any)?.plan;
            const expiry2 = (authUser as any)?.plan_expires_at;
            if (p2 !== 'ultra' && p2 !== 'pro') return null;
            const suffix2 = expiry2 ? ` · ${formatTimeLeft(expiry2, now)} left` : '';
            const tier2 = (authUser as any)?.plan_tier;
            const tierLabel2 = tier2 === 'enterprise' ? ' · Ent' : tier2 === 'smb' ? ' · SMB' : ' · Ind';
            return (
              <div style={{
                padding: '10px 14px',
                background: p2 === 'ultra' ? 'linear-gradient(135deg,#7c3aed,#4f46e5)' : 'linear-gradient(135deg,#6366f1,#3b82f6)',
              }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: '#fff' }}>
                  {p2 === 'ultra' ? '⚡ Ultra' : '✨ Pro'}{tierLabel2}{suffix2}
                </span>
              </div>
            );
          })()}
          <div>
            {(authUser as any)?.plan === 'basic' && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '9px 12px', gap: 10, borderBottom: moduleRows.length > 0 ? '1px solid #f1f5f9' : 'none',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <span style={{ fontSize: 13 }}>📋</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#1f2937', whiteSpace: 'nowrap' }}>
                    Basic Plan
                  </span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', color: '#16a34a', display: 'block' }}>
                    Free Forever
                  </span>
                  {invoiceResetDate && (
                    <span style={{ fontSize: 9.5, color: '#6366f1', fontWeight: 700, fontVariantNumeric: 'tabular-nums', display: 'block' }}>
                      ⚡ {formatQuotaTimeLeft(invoiceResetDate, now)}
                    </span>
                  )}
                </div>
              </div>
            )}
            {moduleRows.map((row, i) => (
              <div key={i} style={{ borderBottom: i < moduleRows.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                {row}
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
})()}
</div>
              </div>
              <div
                onClick={() => {
                  if (window.confirm('Sign out?\n\n✅ All your files are saved on your account.')) onSignOut();
                }}
                title={`${authUser.name} (${authUser.role}) · Click to sign out`}
                style={{
                  width: 34, height: 34, borderRadius: '50%',
                  background: `linear-gradient(135deg, ${roleColor[authUser.role] || '#6366f1'}, ${roleColor[authUser.role] || '#6366f1'}aa)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                {authUser.name?.charAt(0).toUpperCase()}
              </div>
            </>
          ) : (
            <button
              onClick={onSignIn}
              style={{
                padding: '7px 18px', borderRadius: 8, border: 'none',
                background: 'linear-gradient(to right, #6366f1, #8b5cf6)',
                color: '#fff', fontWeight: 600, fontSize: 15, cursor: 'pointer',
              }}
            >
              Sign In
            </button>
          )}
        </div>

      </div>
    </nav>
    </>
  );
}
